import moment from 'moment';
import { IToastr } from '@ds/elements/Toast/Toast.types';
import { getCountryName } from 'src/utils/services/common';
import { Country } from 'src/shared/types/Common';
import { IDocument } from 'src/shared/types/Documents';
import { IImmigrationDocModel, IPassport, IUsedPassportViewModel } from 'src/shared/types/ImmigrationDocument';
import { getDocumentData } from 'src/shared/services/document-api/Documents';
import { DocumentsDownloadType, getDateObject, OLD_DATE } from 'src/utils/constants/common';
import { FileUtil } from 'src/utils/FileUtils';
import { getFormattedDate, UserDateFormat } from 'src/utils/formatters/formatDate';

export const formatPassportInfo = (input: IUsedPassportViewModel, countries: Country[] | undefined): string => {
  if (!input || typeof input !== 'object') return '';

  const parts: string[] = [
    input.country && countries && getCountryName(countries, input.country),
    input.passportNumber,
    formatDateSafely(input.passportExpirationDate),
  ].filter((part): part is string => Boolean(part)); // ensures only strings are included

  return parts.join(' - ');
};

const formatDateSafely = (date?: string | Date): string => {
  try {
    return date ? getFormattedDate(date.toString(), UserDateFormat.get()) : '';
  } catch {
    return '';
  }
};

export const sortPassports = (passports: IPassport[] | undefined) => {
  if (!passports) return [];

  const activePassports = passports.filter((passport) => !passport.isInactive);
  const inActivePassports = passports.filter((passport) => passport.isInactive);

  activePassports.sort((a, b) => {
    const dateA = getDateObject(a.passportExpirationDate || OLD_DATE);
    const dateB = getDateObject(b.passportExpirationDate || OLD_DATE);
    return (dateA?.getTime() || 0) - (dateB?.getTime() || 0);
  });

  inActivePassports.sort((a, b) => {
    const dateA = getDateObject(a.passportExpirationDate || OLD_DATE);
    const dateB = getDateObject(b.passportExpirationDate || OLD_DATE);
    return (dateB?.getTime() || 0) - (dateA?.getTime() || 0);
  });

  return [...activePassports, ...inActivePassports];
};

export enum ImmigrationDocumentStatus {
  Active = 'Active',
  Inactive = 'Inactive',
  Pending = 'Pending',
}

export const evaluateImmigrationDocumentStatus = (doc: IImmigrationDocModel) => {
  if (doc.isInactive) {
    return ImmigrationDocumentStatus.Inactive;
  }

  const nowDate = moment();
  const beginDate = doc.beginDate ? moment(doc.beginDate) : null;
  const expirationDate = doc.expirationDate ? moment(doc.expirationDate) : null;

  if (beginDate && nowDate.isBefore(beginDate)) {
    return ImmigrationDocumentStatus.Pending;
  }

  if (expirationDate && nowDate.isAfter(expirationDate)) {
    return ImmigrationDocumentStatus.Inactive;
  }

  return ImmigrationDocumentStatus.Active;
};

type DownloadParams = {
  doc: IDocument;
  beneId: number | undefined;
  toastRef: React.RefObject<IToastr | null>;
  setIsProcessing: (isProcessing: boolean) => void;
};

export const createDownloadHandler =
  ({ doc, beneId, toastRef, setIsProcessing }: DownloadParams) =>
  async () => {
    if (!doc || !doc.documentApiKey) return;
    setIsProcessing(true);
    try {
      toastRef.current?.show({ message: 'Processing...', severity: 'info', sticky: true });

      const { arrayBuffer, fileName, type } = await getDocumentData({
        downloadType: DocumentsDownloadType.Default,
        documentKeys: [doc.documentApiKey],
        beneId,
        toastRef,
      });

      FileUtil.downloadArrayBuffer(arrayBuffer, fileName, type);
    } catch (error) {
      console.error('Error downloading the document:', error);
    } finally {
      setIsProcessing(false);
      toastRef.current?.clear();
    }
  };
